import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { ChevronDown, LogOut, FileText, Edit, Key, User, ClipboardList } from 'lucide-react';

export default function ClientHeader() {
  const [siteSettings, setSiteSettings] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const user = api.getUser();

  useEffect(() => {
    api.getSiteSettings().then(setSiteSettings).catch(() => {});
  }, []);

  const handleLogout = () => { api.clearAuth(); navigate('/'); setShowMenu(false); };

  // Don't show on admin/agent pages
  if (location.pathname.startsWith('/admin') || location.pathname.startsWith('/agent')) return null;

  const logoSrc = siteSettings?.company_logo_base64 ? `data:image/png;base64,${siteSettings.company_logo_base64}` : null;

  return (
    <header className="border-b border-gray-200 bg-white sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
          {logoSrc ? (
            <img src={logoSrc} alt="Logo" className="h-8 max-w-[120px] object-contain" data-testid="site-logo" />
          ) : (
            <ClipboardList className="w-5 h-5 text-emerald-600" />
          )}
          <span className="text-gray-900 font-bold text-sm hidden sm:block" data-testid="site-title">
            {siteSettings?.landing_hero_title?.split(' ').slice(0, 3).join(' ') || 'ESG Assessment'}
          </span>
        </div>

        <div className="flex items-center gap-3">
          {user ? (
            <div className="relative">
              <button onClick={() => setShowMenu(!showMenu)} data-testid="client-header-menu-btn"
                className="flex items-center gap-2 bg-gray-50 hover:bg-gray-100 rounded-full px-3 py-1.5 transition-colors">
                <div className="w-7 h-7 rounded-full bg-emerald-600 flex items-center justify-center text-white text-xs font-bold">
                  {(user.full_name || user.email || 'U')[0].toUpperCase()}
                </div>
                <span className="text-sm text-gray-700 font-medium hidden sm:block max-w-[120px] truncate">{user.full_name || user.email}</span>
                <ChevronDown className="w-3 h-3 text-gray-400" />
              </button>

              {showMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden" data-testid="client-header-dropdown">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{user.full_name}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                    <div className="py-1">
                      <button onClick={() => { setShowMenu(false); navigate('/dashboard'); }} data-testid="nav-my-dashboard"
                        className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-gray-400" /> My Assessments
                      </button>
                      <button onClick={() => { setShowMenu(false); navigate('/'); }} data-testid="nav-new-assessment"
                        className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                        <Edit className="w-4 h-4 text-gray-400" /> New Assessment
                      </button>
                      <button onClick={() => { setShowMenu(false); navigate('/profile'); }} data-testid="nav-profile"
                        className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                        <Key className="w-4 h-4 text-gray-400" /> Change Password
                      </button>
                    </div>
                    <div className="border-t border-gray-100 py-1">
                      <button onClick={handleLogout} data-testid="header-logout-btn"
                        className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                        <LogOut className="w-4 h-4" /> Sign Out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <button onClick={() => navigate('/login')} data-testid="header-login-btn"
                className="text-sm text-gray-600 hover:text-gray-900 font-medium px-3 py-1.5 transition-colors">
                Login
              </button>
              <button onClick={() => navigate('/')} data-testid="header-register-btn"
                className="text-sm bg-emerald-600 hover:bg-emerald-700 text-white font-medium px-4 py-1.5 rounded-lg transition-colors">
                Start Assessment
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
